import { observer } from "mobx-react-lite";
import pageBuilderStore from "../../stores/pageBuilderStore";
import contentManager from "../../stores/content-manager";
import serializationManager from "../../stores/serialization-manager";
import PreviewToolbar from "./preview-toolbar";

/**
 * PagePreview – Live Responsive Preview Canvas for PageBuilder
 *
 * @component
 * @description
 * Renders a fully responsive, live preview of the current page or email content as it would appear
 * to end users. Supports device width switching (desktop, tablet, phone), and always reflects
 * the latest HTML generated by the builder’s serialization logic.
 *
 * @features
 * - Displays current content using the latest HTML serialization (WYSIWYG)
 * - Integrates with PreviewToolbar for device and width switching
 * - Supports desktop, tablet, and phone widths (user-selectable)
 * - Uses MobX state for preview mode and selected width
 * - Scrollable and visually isolated from the editing interface
 *
 * @remarks
 * - Internal-use component for the PageBuilder; not intended for standalone use
 * - Uses `dangerouslySetInnerHTML` to inject builder-generated HTML
 * - Preview styling (background, padding, border) matches end-user environment
 *
 * @example
 * <PagePreview />
 *
 * @since 2025-06-09
 * @lastUpdated 2025-07-15
 * @author
 * d2d-weizhi (Chen Weizhi)
 */
export default observer(function PagePreview() {
  const htmlOutput = serializationManager.fromBlocksToHTML(contentManager.getDesignContent());

  // Determine current width to preview in
  const { selectedWidth, deviceMode } = pageBuilderStore.previewMode;
  const fallbackWidths: Record<string, string> = {
    desktop: "1280px",
    tablet: "768px",
    phone: "375px",
  };

  const contentWidth = selectedWidth || fallbackWidths[deviceMode] || "1280px";

  return (
    <div className="flex flex-col w-full h-full bg-gray-100 border border-gray-300 mt-8">
      {/* Shared PreviewToolbar handles layout switching */}
      <PreviewToolbar />

      {/* Preview Canvas */}
      <div 
        className="flex-1 overflow-y-auto p-8 flex items-start justify-center bg-gray-100 z-20"
        style={{
          width: "1600px"
        }}
      >
        <div
          className="bg-white flex flex-col gap-4 shadow-xl mx-auto border border-gray-300 rounded-md p-4"
          style={{
            width: contentWidth,
            minHeight: "100%",
          }}
          dangerouslySetInnerHTML={{ __html: htmlOutput }}
        />
      </div>
    </div>
  );
});
